import { ColDef } from "ag-grid-community";
import { SyntheticEvent } from "react";
import { ButtonTypes } from "../../../../../../components/ui/Button";
import { IconSymbols } from "../../../../../../components/ui/Icon";
import { DateColumnBuilder } from "../../../../../../core/grids/builder/directorBuilder/columns/commonColumns/DateColumn/DateColumn_builder";
import { DATE_FILTER_CONFIG } from "../../../../../../core/grids/builder/directorBuilder/columns/commonColumns/DateColumn/DateColumn_config";
import { MasterDetailRowColumnBuilder } from "../../../../../../core/grids/builder/directorBuilder/columns/commonColumns/MasterDetailRowColumn/MasterDetailRowColumn_builder";
import { NameColumnBuilder } from "../../../../../../core/grids/builder/directorBuilder/columns/commonColumns/NameColumn/NameColumn_builder";
import {
  NAME_COLUMN_CONFIG,
  NAME_FILTER_CONFIG
} from "../../../../../../core/grids/builder/directorBuilder/columns/commonColumns/NameColumn/NameColumn_config";
import { SelectionColumnBuilder } from "../../../../../../core/grids/builder/directorBuilder/columns/commonColumns/SelectionColumn/SelectionColumn_builder";
import { UiActionColumnBuilder } from "../../../../../../core/grids/builder/directorBuilder/columns/commonColumns/UiActionColumn/UiActionColumn_builder";
import { BaseGridColumnBuilder } from "../../../../../../core/grids/builder/directorBuilder/grids/base/BaseGridColumnBuilder";
import { AppGridToolbarType } from "../../../../../../core/grids/builder/directorBuilder/types/AppGrid_types";
import { CommonColDefFieldNamesEnum } from "../../../../../../core/grids/builder/enums/AgGridColDefFieldNameEnum";
import I18n from "../../../../../../core/localization/I18n";
import { UiActionRenderers } from "../../../../../../core/uiAction/IUiAction";
import { PlaceGroupsField, UiSizes } from "../../../../../../enums";
import { PlacesGroupsApi } from "../../../../../../services/api/v2/places/PlacesGroups.api";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import { SHOW_ADD_PLACES_MODAL } from "./PlacesGroupsGrid_modals";

export interface PlacesGridColumnBuilderProps {
  canEdit: boolean;
  organisationId: number;
  projectId: number;
  placeGroupId: number;
  columns?: string[];
  placesGroupsProvider: PlacesGroupsApi;
  gridToolbarType: AppGridToolbarType;
  onFieldUpdate: () => void;
  onRowExpanded: () => any;
}

export class PlacesGroupsGridColumnBuilder extends BaseGridColumnBuilder {
  gridColumns: Dictionary<ColDef>;
  gridToastService = GridToastService;
  gridProps: PlacesGridColumnBuilderProps;
  columnDefs: Dictionary<(header?: string) => ColDef>;
  columns: string;
  organisationId: number;
  placeGroupId: number;
  gridToolbarType: AppGridToolbarType;
  reloadData: () => void;
  onRowExpanded: () => void;

  constructor(gridProps: PlacesGridColumnBuilderProps) {
    super(gridProps.placesGroupsProvider.updatePlaceGroupField, gridProps.organisationId, null, gridProps.canEdit);
    this.gridProps = gridProps;
    this.organisationId = gridProps.organisationId;
    this.gridToolbarType = gridProps.gridToolbarType;
    this.placeGroupId = gridProps.placeGroupId;
    this.reloadData = gridProps.onFieldUpdate;
    this.init();
  }
  private init = () => {
    this.columnDefs = {
      [CommonColDefFieldNamesEnum.Selected]: () =>
        new SelectionColumnBuilder().makeSelectable().generateColumnOptions(),
      [CommonColDefFieldNamesEnum.Name]: () => this.buildNameColumn(),
      [CommonColDefFieldNamesEnum.CreatedBy]: (header?: string) =>
        new NameColumnBuilder({
          field: CommonColDefFieldNamesEnum.CreatedBy,
          headerName: header || I18n.t("grids.createdBy"),
          pinned: false
        })
          .makeEditable(false)
          .makeReadOnly()
          .setFilterOptions(NAME_FILTER_CONFIG)
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.CreatedAt]: (header?: string) =>
        new DateColumnBuilder({
          field: CommonColDefFieldNamesEnum.CreatedAt,
          headerName: header || I18n.t("grids.createdOn")
        })
          .makeEditable(false)
          .makeReadOnly()
          .withCellEditor(CommonColDefFieldNamesEnum.CreatedAt, "")
          .withComparator()
          .setValueFormatter(CommonColDefFieldNamesEnum.CreatedAt)
          .setFilterOptions(DATE_FILTER_CONFIG)
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.ModifiedBy]: (header?: string) =>
        new NameColumnBuilder({
          field: CommonColDefFieldNamesEnum.ModifiedBy,
          headerName: header || I18n.t("grids.lastModifiedBy"),
          pinned: false
        })
          .makeEditable(false)
          .makeReadOnly()
          .setFilterOptions(NAME_FILTER_CONFIG)
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.UpdatedAt]: (header?: string) =>
        new DateColumnBuilder({
          field: CommonColDefFieldNamesEnum.UpdatedAt,
          headerName: header || I18n.t("grids.lastModifiedOn")
        })
          .makeEditable(false)
          .makeReadOnly()
          .withCellEditor(CommonColDefFieldNamesEnum.UpdatedAt, "")
          .withComparator()
          .setValueFormatter(CommonColDefFieldNamesEnum.UpdatedAt)
          .setFilterOptions(DATE_FILTER_CONFIG)
          .generateColumnOptions()
    };

    if (this.gridToolbarType !== "link-modal") {
      this.columnDefs[CommonColDefFieldNamesEnum.UiActionCol] = (header?: string) =>
        new UiActionColumnBuilder({
          field: CommonColDefFieldNamesEnum.UiActionCol,
          headerName: "",
          // pinned: "right",
          width: 64
        })
          .makeEditable(this.canEdit)
          .makeReadOnly(!this.canEdit)
          .makeSelectable(this.canEdit)
          .setActions([
            {
              id: "add",
              label: I18n.t("phrases.addPlaces"),
              onAction: this.showLinkPlacesModal,
              componentProps: {
                className: "mt-2",
                type: ButtonTypes.PRIMARY,
                symbol: IconSymbols.Plus,
                size: UiSizes.XXS,
                isDisabled: !this.canEdit
              },
              rendersIn: UiActionRenderers.BUTTON_ICON
            }
          ])
          .generateColumnOptions();
    }
  };

  showLinkPlacesModal = (k, i) => {
    this.gridModalBuilder.constructPopupModal().setModalOptions(SHOW_ADD_PLACES_MODAL(k, i)).generateModal();
  };

  generateColumnDefs = (): ColDef[] => {
    let res: ColDef[] = [];

    this.gridProps.columns.forEach(e => {
      const col = this.columnDefs[e];
      if (typeof col === "function") {
        res.push(col());
      } else {
        console.warn(`Typeof columnDef with name "${this.columnDefs}" is not a function`);
      }
    });

    return res;
  };

  buildNameColumn = () => {
    let model = new MasterDetailRowColumnBuilder({
      field: CommonColDefFieldNamesEnum.Name,
      headerName: I18n.t("phrases.name"),
      pinned: "left",
      resizable: true,
      width: 100
    })
      .setColumnOptions(NAME_COLUMN_CONFIG({ headerName: "Name" }))
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCustomActionsCellRenderer(CommonColDefFieldNamesEnum.Name, this.generateViewChildGridAction)
      .useExpandedMasterHeaderHighlighting([CommonColDefFieldNamesEnum.Name])
      .setFilterOptions(NAME_FILTER_CONFIG);

    if (this.gridProps.canEdit) {
      model.createValueSetter(this.updateName);
    }

    return model.generateColumnOptions();
  };
  generateViewChildGridAction = (
    value: string,
    isExpanded: boolean,
    classname: string,
    onclick: (ev: SyntheticEvent) => void
  ) => {
    return (
      <>
        {isExpanded ? (
          <span className={`ag-group-expanded pointer-class ${classname}-view`} onClick={onclick}>
            <span className="ag-icon ag-icon-tree-open" unselectable="on" role="presentation"></span>
          </span>
        ) : (
          <span className={`ag-group-contracted pointer-class ${classname}-view`} onClick={onclick}>
            <span className="ag-icon ag-icon-tree-closed" unselectable="on" role="presentation"></span>
          </span>
        )}
        <span>{value}</span>
      </>
    );
  };
  updateName = async (entityId: number, text: string) => {
    await this.updateTextField(PlaceGroupsField.name, entityId, text);
    await this.reloadData();
  };
}
